import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Button, Icon, Badge, Switch, Table, Thead, Tbody, Tr, Th, Td,
  useToast, Divider, useColorModeValue, Heading, Input, Select, Spinner, Code,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  SimpleGrid, Stat, StatLabel, StatNumber, FormControl, FormLabel, FormHelperText,
} from '@chakra-ui/react';
import { FiZap, FiSettings, FiActivity, FiRefreshCw, FiCheck, FiX, FiUsers, FiDownloadCloud } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useCrmUsers } from '../../../hooks/useCrmUsers';

function HealthStat({ label, value, color }) {
  return (
    <Stat>
      <StatLabel fontSize="10px" color="gray.500">{label}</StatLabel>
      <StatNumber fontSize="md" color={color}>{value ?? '--'}</StatNumber>
    </Stat>
  );
}

function CompanySyncSettings() {
  const { users: crmUsers, loading: usersLoading } = useCrmUsers();
  const [settings, setSettings] = useState(null);
  const [syncUser, setSyncUser] = useState(null);
  const [counters, setCounters] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [allowlistDraft, setAllowlistDraft] = useState('');
  const [excludeActiveDraft, setExcludeActiveDraft] = useState('');
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const toast = useToast();
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const resultBg = useColorModeValue('gray.50', 'gray.900');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'getAriveSyncSettings');
      const result = await fn();
      setSettings(result.data?.settings || null);
      setSyncUser(result.data?.syncUser || null);
      setCounters(result.data?.counters || {});
      setAllowlistDraft((result.data?.settings?.auto_create_originator_allowlist || []).join(', '));
      setExcludeActiveDraft((result.data?.settings?.backfill_exclude_active_originators || []).join(', '));
    } catch (err) {
      toast({ title: 'Failed to load sync settings', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch, fieldLabel) => {
    setSaving(fieldLabel);
    try {
      const fn = httpsCallable(functionsUsEast1, 'updateAriveSyncSettings');
      const result = await fn(patch);
      setSettings(result.data?.settings || null);
      toast({ title: `${fieldLabel} updated`, status: 'success', duration: 2500 });
      if (patch.sync_user_id) load();
    } catch (err) {
      toast({ title: `Failed to update ${fieldLabel}`, description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(null);
    }
  };

  const runBackfill = async (dryRun) => {
    setBackfillRunning(true);
    setBackfillResult(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'backfillAriveDeals', { timeout: 540000 });
      const result = await fn({ dryRun });
      setBackfillResult(result.data);
      toast({
        title: dryRun ? 'Dry-run complete' : 'Backfill complete',
        status: 'success',
        duration: 5000,
      });
      if (!dryRun) load();
    } catch (err) {
      toast({ title: 'Backfill failed', description: err.message, status: 'error', duration: 8000 });
    } finally {
      setBackfillRunning(false);
    }
  };

  if (loading) return <Spinner size="sm" />;
  if (!settings) return <Text fontSize="sm" color={mutedColor}>Sync settings row not found. Run the 20260803 migration.</Text>;

  return (
    <VStack align="stretch" spacing={4}>
      {/* Sync user */}
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Company Sync User (payroll specialist)</FormLabel>
        <HStack>
          <Select
            size="sm"
            maxW="340px"
            value={settings.sync_user_id || ''}
            isDisabled={usersLoading || saving === 'Sync user'}
            onChange={(e) => e.target.value && save({ sync_user_id: e.target.value }, 'Sync user')}
          >
            <option value="">Select user...</option>
            {crmUsers.map((u) => (
              <option key={u.uid} value={u.uid}>{u.name || u.email} ({u.email})</option>
            ))}
          </Select>
          {syncUser && (
            <Badge colorScheme={syncUser.hasCredentials ? 'green' : 'red'} fontSize="10px">
              {syncUser.hasCredentials ? 'Credentials OK' : 'No credentials'}
            </Badge>
          )}
        </HStack>
        <FormHelperText fontSize="10px">
          All company-wide sync (webhooks, polling, outbound pushes) runs under this user's Arive API key.
        </FormHelperText>
      </FormControl>

      <Divider />

      {/* Master switches */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <HStack justify="space-between">
          <Text fontSize="xs">Inbound sync (Arive → Origin)</Text>
          <Switch
            size="sm"
            isChecked={settings.inbound_enabled}
            isDisabled={saving === 'Inbound sync'}
            onChange={(e) => save({ inbound_enabled: e.target.checked }, 'Inbound sync')}
          />
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs">Outbound sync (Origin → Arive)</Text>
          <Switch
            size="sm"
            isChecked={settings.outbound_enabled}
            isDisabled={saving === 'Outbound sync'}
            onChange={(e) => save({ outbound_enabled: e.target.checked }, 'Outbound sync')}
          />
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs">Auto-create Origin deals</Text>
          <Switch
            size="sm"
            isChecked={settings.inbound_auto_create_deals}
            isDisabled={saving === 'Auto-create'}
            onChange={(e) => save({ inbound_auto_create_deals: e.target.checked }, 'Auto-create')}
          />
        </HStack>
      </SimpleGrid>

      {/* Allowlist + scope */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Auto-create originator allowlist</FormLabel>
          <HStack>
            <Input
              size="sm"
              value={allowlistDraft}
              placeholder="Empty = all originators"
              onChange={(e) => setAllowlistDraft(e.target.value)}
            />
            <Button
              size="sm"
              isLoading={saving === 'Allowlist'}
              onClick={() => save({
                auto_create_originator_allowlist: allowlistDraft.split(',').map((s) => s.trim()).filter(Boolean),
              }, 'Allowlist')}
            >
              Save
            </Button>
          </HStack>
          <FormHelperText fontSize="10px">Comma-separated originator emails. Deals are only auto-created for these LOs.</FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Backfill scope</FormLabel>
          <Select
            size="sm"
            maxW="200px"
            value={settings.backfill_scope || 'active'}
            isDisabled={saving === 'Backfill scope'}
            onChange={(e) => save({ backfill_scope: e.target.value }, 'Backfill scope')}
          >
            <option value="active">Active pipeline only</option>
            <option value="all">Full history</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Backfill: exclude active files for</FormLabel>
          <HStack>
            <Input
              size="sm"
              value={excludeActiveDraft}
              placeholder="Empty = no exclusions"
              onChange={(e) => setExcludeActiveDraft(e.target.value)}
            />
            <Button
              size="sm"
              isLoading={saving === 'Backfill exclusions'}
              onClick={() => save({
                backfill_exclude_active_originators: excludeActiveDraft.split(',').map((s) => s.trim()).filter(Boolean),
              }, 'Backfill exclusions')}
            >
              Save
            </Button>
          </HStack>
          <FormHelperText fontSize="10px">
            The backfill skips these LOs' open (non-funded) Arive files — for house accounts holding old migrated apps.
            Funded files still import under Full history, and live auto-create is unaffected.
          </FormHelperText>
        </FormControl>
      </SimpleGrid>

      <Divider />

      {/* Health */}
      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">Health</Text>
          <Button size="xs" variant="ghost" leftIcon={<Icon as={FiRefreshCw} />} onClick={load}>Refresh</Button>
        </HStack>
        <SimpleGrid columns={{ base: 2, md: 6 }} spacing={2}>
          <HealthStat label="Mirror rows" value={counters.mirrorRows?.toLocaleString?.() ?? counters.mirrorRows} />
          <HealthStat label="Linked to deals" value={counters.linkedRows?.toLocaleString?.() ?? counters.linkedRows} />
          <HealthStat label="Queue pending" value={counters.queuePending} color={counters.queuePending > 50 ? 'orange.400' : undefined} />
          <HealthStat label="Errors (24h)" value={counters.errors24h} color={counters.errors24h > 0 ? 'red.400' : 'green.400'} />
          <HealthStat label="No-owner skips (24h)" value={counters.skippedNoOwner24h} />
          <HealthStat label="Snapshots (24h)" value={counters.snapshots24h} color={counters.snapshots24h === 0 ? 'orange.400' : undefined} />
        </SimpleGrid>
        <HStack spacing={4} mt={2}>
          <Text fontSize="10px" color={mutedColor}>
            Loans cursor: {settings.loans_cursor ? new Date(settings.loans_cursor).toLocaleString() : 'not set'}
          </Text>
          <Text fontSize="10px" color={mutedColor}>
            Leads cursor: {settings.leads_cursor ? new Date(settings.leads_cursor).toLocaleString() : 'not set'}
          </Text>
        </HStack>
      </Box>

      <Divider />

      {/* Backfill */}
      <Box>
        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" mb={2}>Backfill</Text>
        <HStack>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FiDownloadCloud} />}
            onClick={() => runBackfill(true)}
            isLoading={backfillRunning}
          >
            Dry Run
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<Icon as={FiDownloadCloud} />}
            onClick={() => runBackfill(false)}
            isLoading={backfillRunning}
          >
            Run Backfill
          </Button>
        </HStack>
        {backfillResult && (
          <Box mt={2} p={2} bg={resultBg} borderRadius="md" maxH="260px" overflowY="auto">
            <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(backfillResult, null, 2)}
            </pre>
          </Box>
        )}
      </Box>
    </VStack>
  );
}

function ConnectionTest() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const toast = useToast();

  const test = async () => {
    setTesting(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'searchAriveLoans');
      await fn({ email: 'connection-test@test.com' });
      setResult('success');
      toast({ title: 'Connection successful', status: 'success', duration: 3000 });
    } catch (err) {
      setResult('error');
      toast({ title: 'Connection failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setTesting(false);
    }
  };

  return (
    <HStack>
      <Button size="sm" leftIcon={<Icon as={FiZap} />} onClick={test} isLoading={testing} colorScheme="blue">
        Test Arive Connection
      </Button>
      {result === 'success' && <Badge colorScheme="green">Connected</Badge>}
      {result === 'error' && <Badge colorScheme="red">Failed</Badge>}
    </HStack>
  );
}

function WebhookSetup() {
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const setup = async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'setupAriveWebhooks');
      const result = await fn();
      toast({ title: 'Webhooks configured', description: result.data?.message, status: 'success', duration: 5000 });
    } catch (err) {
      toast({ title: 'Setup failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button size="sm" leftIcon={<Icon as={FiSettings} />} onClick={setup} isLoading={loading} colorScheme="purple">
      Subscribe to All Webhook Events
    </Button>
  );
}

function FeatureToggles() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    const { data } = await supabase.from('arive_integration_config').select('*').order('feature').order('event_type');
    setConfigs(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, currentEnabled) => {
    await supabase.from('arive_integration_config').update({ is_enabled: !currentEnabled }).eq('id', id);
    load();
  };

  if (loading) return <Spinner size="sm" />;

  const groupedByFeature = configs.reduce((acc, c) => {
    if (!acc[c.feature]) acc[c.feature] = [];
    acc[c.feature].push(c);
    return acc;
  }, {});

  return (
    <VStack align="stretch" spacing={3}>
      {Object.entries(groupedByFeature).map(([feature, items]) => (
        <Box key={feature}>
          <Text fontSize="sm" fontWeight="bold" textTransform="uppercase" mb={1}>{feature}</Text>
          {items.map((cfg) => (
            <HStack key={cfg.id} justify="space-between" py={1}>
              <Text fontSize="xs">{cfg.event_type}</Text>
              <Switch size="sm" isChecked={cfg.is_enabled} onChange={() => toggle(cfg.id, cfg.is_enabled)} />
            </HStack>
          ))}
        </Box>
      ))}
    </VStack>
  );
}

function SyncLogViewer() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ feature: '', status: '' });
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const load = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    setLoading(true);
    let query = supabase.from('arive_sync_log').select('*').order('created_at', { ascending: false }).limit(50);
    if (filter.feature) query = query.eq('feature', filter.feature);
    if (filter.status) query = query.eq('status', filter.status);
    const { data } = await query;
    setLogs(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <VStack align="stretch" spacing={2}>
      <HStack>
        <Select size="xs" placeholder="All features" value={filter.feature} onChange={(e) => setFilter({ ...filter, feature: e.target.value })}>
          <option value="crm">CRM</option>
          <option value="ops">Ops</option>
          <option value="comp">Comp</option>
          <option value="ratelock">Rate Lock</option>
          <option value="system">System</option>
        </Select>
        <Select size="xs" placeholder="All statuses" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="skipped">Skipped</option>
        </Select>
        <Button size="xs" leftIcon={<Icon as={FiRefreshCw} />} onClick={load} isLoading={loading}>Refresh</Button>
      </HStack>
      <Box overflowX="auto" maxH="400px" overflowY="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th fontSize="10px">Time</Th>
              <Th fontSize="10px">Feature</Th>
              <Th fontSize="10px">Dir</Th>
              <Th fontSize="10px">Event</Th>
              <Th fontSize="10px">Status</Th>
              <Th fontSize="10px">Arive ID</Th>
            </Tr>
          </Thead>
          <Tbody>
            {logs.map((log) => (
              <Tr key={log.id}>
                <Td fontSize="10px" color={mutedColor}>{new Date(log.created_at).toLocaleString()}</Td>
                <Td fontSize="10px">{log.feature}</Td>
                <Td fontSize="10px">{log.direction}</Td>
                <Td fontSize="10px">{log.event_type}</Td>
                <Td>
                  <Badge fontSize="9px" colorScheme={log.status === 'success' ? 'green' : log.status === 'error' ? 'red' : 'gray'}>
                    {log.status}
                  </Badge>
                </Td>
                <Td fontSize="10px">{log.arive_entity_id}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
}

export default function AriveAdminPanel() {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={bg} p={5}>
      <VStack align="stretch" spacing={4}>
        <Heading size="md">Arive LOS Integration</Heading>

        <Accordion allowMultiple defaultIndex={[0]}>
          <AccordionItem border="none">
            <AccordionButton px={0}>
              <HStack flex="1"><Icon as={FiUsers} /><Text fontWeight="semibold" fontSize="sm">Company Sync (Payroll Specialist)</Text></HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0}>
              <CompanySyncSettings />
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton px={0}>
              <HStack flex="1"><Icon as={FiZap} /><Text fontWeight="semibold" fontSize="sm">Connection</Text></HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0}>
              <VStack align="stretch" spacing={3}>
                <ConnectionTest />
                <Divider />
                <WebhookSetup />
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton px={0}>
              <HStack flex="1"><Icon as={FiSettings} /><Text fontWeight="semibold" fontSize="sm">Feature Event Toggles</Text></HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0}>
              <FeatureToggles />
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton px={0}>
              <HStack flex="1"><Icon as={FiActivity} /><Text fontWeight="semibold" fontSize="sm">Sync Log</Text></HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0}>
              <SyncLogViewer />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </Box>
  );
}
